import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Car, Plus, Wrench, Key, AlertTriangle, CheckCircle, Clock, Fuel, MapPin,
    TrendingDown, Building2, Users, Edit2, Trash2, Phone, Mail, Star, X,
    Loader2, AlertCircle, Zap, Droplets, Wind, CalendarDays, ChevronLeft,
    ChevronRight, Info, ChevronDown, Briefcase, Unlink, History, ArrowUpRight,
} from 'lucide-react';
import Layout from './Layout';
import LoanCarHistoryModal from './LoanCarHistoryModal';
import { getResourceJobAllocationsApi, unassignResourceFromJobApi } from '../services/resourceApi';
import {
    fetchLoanCars, fetchEquipment, fetchTools, fetchSpaces, fetchConsultants,
    createLoanCar, updateLoanCar, deleteLoanCar,
    createEquipment, updateEquipment, deleteEquipment,
    createTool, updateTool, deleteTool,
    createSpace, updateSpace, deleteSpace,
    createConsultant, updateConsultant, deleteConsultant,
    setActiveTab, openCreateModal, closeCreateModal, openEditModal, closeEditModal,
    openDeleteConfirm, closeDeleteConfirm, clearOperationStatus,
    openScheduleModal, closeScheduleModal,
    openHistoryModal, closeHistoryModal,
    fetchLoanCarAllocations,
    fetchLoanCarIncidentHistory,
    fetchLoanCarRegoStatuses,
} from '../store/slices/resourceSlice';

/**
 * ResourcesPage - Resource planning and management
 * Manages Loan Cars, Equipment, Tools, Spaces, and Consultants
 */
const ResourcesPage = () => {
    const dispatch = useDispatch();
    const {
        loanCars, loanCarsLoading,
        equipment, equipmentLoading,
        tools, toolsLoading,
        spaces, spacesLoading,
        consultants, consultantsLoading,
        activeTab, showCreateModal, showEditModal, showDeleteConfirm, itemToDelete,
        selectedLoanCar, selectedEquipment, selectedTool, selectedSpace, selectedConsultant,
        operationLoading, operationError, operationSuccess,
        showScheduleModal, scheduleCarDetails, allocations, allocationsLoading, allocationsError,
        showHistoryModal, historyCarDetails,
        historyIncidents, historyPage, historyTotalPages, historyTotalElements,
        historyLoading, historyError,
        loanCarRegoStatuses,
    } = useSelector(state => state.resources);

    useEffect(() => {
        switch (activeTab) {
            case 'loan-cars':
                dispatch(fetchLoanCars()).then((action) => {
                    if (action.payload?.length) {
                        dispatch(fetchLoanCarRegoStatuses(action.payload.map(c => c.rego)));
                    }
                });
                break;
            case 'equipment': dispatch(fetchEquipment()); break;
            case 'tools': dispatch(fetchTools()); break;
            case 'spaces': dispatch(fetchSpaces()); break;
            case 'consultants': dispatch(fetchConsultants()); break;
        }
    }, [activeTab, dispatch]);

    useEffect(() => {
        if (operationSuccess) {
            const timer = setTimeout(() => dispatch(clearOperationStatus()), 3000);
            return () => clearTimeout(timer);
        }
    }, [operationSuccess, dispatch]);

    const [showAllSchedule, setShowAllSchedule] = useState(false);

    const tabs = [
        { id: 'loan-cars', label: 'Loan Cars', icon: Key },
        // { id: 'equipment', label: 'Equipment', icon: Wrench }, // Hidden for now
        // { id: 'tools', label: 'Tools', icon: TrendingDown },   // Hidden for now
        { id: 'spaces', label: 'Spaces', icon: Building2 },
        { id: 'consultants', label: 'Consultants', icon: Users },
    ];

    const getStatusBadge = (status) => {
        const styles = {
            AVAILABLE: 'bg-emerald-100 text-emerald-700',
            ALLOCATED: 'bg-blue-100 text-blue-700',
            MAINTENANCE: 'bg-amber-100 text-amber-700',
            UNAVAILABLE: 'bg-gray-100 text-gray-700',
            RETIRED: 'bg-red-100 text-red-700',
        };
        const icons = {
            AVAILABLE: <CheckCircle size={12} />,
            ALLOCATED: <Clock size={12} />,
            MAINTENANCE: <Wrench size={12} />,
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.UNAVAILABLE}`}>
                {icons[status]}
                {status?.toLowerCase().replace('_', ' ')}
            </span>
        );
    };

    const getConditionBadge = (condition) => {
        const styles = {
            EXCELLENT: 'bg-emerald-100 text-emerald-700',
            GOOD: 'bg-blue-100 text-blue-700',
            FAIR: 'bg-amber-100 text-amber-700',
            POOR: 'bg-orange-100 text-orange-700',
            NEEDS_REPLACEMENT: 'bg-red-100 text-red-700',
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${styles[condition] || 'bg-gray-100 text-gray-700'}`}>
                {condition?.toLowerCase().replace('_', ' ')}
            </span>
        );
    };

    const handleEdit = (item) => dispatch(openEditModal({ type: activeTab, item }));
    const handleDelete = (item) => dispatch(openDeleteConfirm({ type: activeTab, item }));

    const handleShowSchedule = (car) => {
        dispatch(openScheduleModal(car));
        dispatch(fetchLoanCarAllocations(car.uuid));
    };

    const handleShowHistory = (car) => {
        dispatch(openHistoryModal(car));
        dispatch(fetchLoanCarIncidentHistory({ rego: car.rego, page: 0 }));
    };

    const confirmDelete = () => {
        if (!itemToDelete) return;
        const { type, item } = itemToDelete;
        switch (type) {
            case 'loan-cars': dispatch(deleteLoanCar(item.uuid)); break;
            case 'equipment': dispatch(deleteEquipment(item.uuid)); break;
            case 'tools': dispatch(deleteTool(item.uuid)); break;
            case 'spaces': dispatch(deleteSpace(item.uuid)); break;
            case 'consultants': dispatch(deleteConsultant(item.uuid)); break;
        }
    };

    const isLoading = () => {
        switch (activeTab) {
            case 'loan-cars': return loanCarsLoading;
            case 'equipment': return equipmentLoading;
            case 'tools': return toolsLoading;
            case 'spaces': return spacesLoading;
            case 'consultants': return consultantsLoading;
            default: return false;
        }
    };

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-cyan-50 rounded-xl flex items-center justify-center">
                            <Car size={20} className="text-cyan-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Resource Planning</h1>
                            <p className="text-sm text-gray-500">Manage loan cars, equipment, tools, spaces, and consultants</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {activeTab === 'loan-cars' && (
                            <button
                                onClick={() => setShowAllSchedule(true)}
                                title="View all loan car schedules"
                                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition-colors font-medium"
                            >
                                <CalendarDays size={18} />
                                <span className="hidden sm:inline">All Schedules</span>
                            </button>
                        )}
                        {activeTab !== 'equipment' && activeTab !== 'tools' && (
                            <button
                                onClick={() => dispatch(openCreateModal())}
                                className="flex items-center gap-2 px-4 py-2.5 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors font-medium"
                            >
                                <Plus size={18} />
                                Add {tabs.find(t => t.id === activeTab)?.label.slice(0, -1)}
                            </button>
                        )}
                    </div>
                </div>

                {/* Success/Error Messages */}
                {operationSuccess && (
                    <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-emerald-700">
                        <CheckCircle size={18} />
                        {operationSuccess}
                    </div>
                )}
                {operationError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
                        <AlertCircle size={18} />
                        {operationError}
                    </div>
                )}

                {/* Tabs */}
                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit overflow-x-auto">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => dispatch(setActiveTab(tab.id))}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                                activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Loading State */}
                {isLoading() && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="animate-spin text-cyan-600" size={32} />
                    </div>
                )}

                {activeTab === 'loan-cars' && !loanCarsLoading && (
                    <LoanCarsTab
                        loanCars={loanCars}
                        regoStatuses={loanCarRegoStatuses}
                        getStatusBadge={getStatusBadge}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onShowHistory={handleShowHistory}
                    />
                )}
                {activeTab === 'equipment' && !equipmentLoading && (
                    <EquipmentTab equipment={equipment} getStatusBadge={getStatusBadge} onEdit={handleEdit} onDelete={handleDelete} />
                )}
                {activeTab === 'tools' && !toolsLoading && (
                    <ToolsTab tools={tools} getConditionBadge={getConditionBadge} onEdit={handleEdit} onDelete={handleDelete} />
                )}
                {activeTab === 'spaces' && !spacesLoading && (
                    <SpacesTab spaces={spaces} getStatusBadge={getStatusBadge} onEdit={handleEdit} onDelete={handleDelete} />
                )}
                {activeTab === 'consultants' && !consultantsLoading && (
                    <ConsultantsTab consultants={consultants} getStatusBadge={getStatusBadge} onEdit={handleEdit} onDelete={handleDelete} />
                )}

                {/* Create Modal */}
                {showCreateModal && (
                    <ResourceModal
                        type={activeTab}
                        onClose={() => dispatch(closeCreateModal())}
                        onSubmit={(data) => {
                            switch (activeTab) {
                                case 'loan-cars': dispatch(createLoanCar(data)); break;
                                case 'equipment': dispatch(createEquipment(data)); break;
                                case 'tools': dispatch(createTool(data)); break;
                                case 'spaces': dispatch(createSpace(data)); break;
                                case 'consultants': dispatch(createConsultant(data)); break;
                            }
                        }}
                        loading={operationLoading}
                        error={operationError}
                    />
                )}

                {/* Edit Modal */}
                {showEditModal && (
                    <ResourceModal
                        type={activeTab}
                        item={selectedLoanCar || selectedEquipment || selectedTool || selectedSpace || selectedConsultant}
                        onClose={() => dispatch(closeEditModal())}
                        onSubmit={(data) => {
                            const item = selectedLoanCar || selectedEquipment || selectedTool || selectedSpace || selectedConsultant;
                            switch (activeTab) {
                                case 'loan-cars': dispatch(updateLoanCar({ uuid: item.uuid, data })); break;
                                case 'equipment': dispatch(updateEquipment({ uuid: item.uuid, data })); break;
                                case 'tools': dispatch(updateTool({ uuid: item.uuid, data })); break;
                                case 'spaces': dispatch(updateSpace({ uuid: item.uuid, data })); break;
                                case 'consultants': dispatch(updateConsultant({ uuid: item.uuid, data })); break;
                            }
                        }}
                        loading={operationLoading}
                        error={operationError}
                    />
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <DeleteConfirmModal
                        item={itemToDelete?.item}
                        onClose={() => dispatch(closeDeleteConfirm())}
                        onConfirm={confirmDelete}
                        loading={operationLoading}
                    />
                )}

                {/* Schedule Modal */}
                {showScheduleModal && (
                    <LoanCarScheduleModal
                        car={scheduleCarDetails}
                        allocations={allocations}
                        loading={allocationsLoading}
                        error={allocationsError}
                        onClose={() => dispatch(closeScheduleModal())}
                    />
                )}

                {/* All Loan Cars Schedule Modal */}
                {showAllSchedule && (
                    <AllLoanCarsScheduleModal
                        loanCars={loanCars}
                        onClose={() => setShowAllSchedule(false)}
                    />
                )}

                {/* History Modal */}
                {showHistoryModal && (
                    <LoanCarHistoryModal
                        car={historyCarDetails}
                        incidents={historyIncidents}
                        page={historyPage}
                        totalPages={historyTotalPages}
                        totalElements={historyTotalElements}
                        loading={historyLoading}
                        error={historyError}
                        onPageChange={(p) =>
                            dispatch(fetchLoanCarIncidentHistory({ rego: historyCarDetails.rego, page: p }))
                        }
                        onClose={() => dispatch(closeHistoryModal())}
                    />
                )}
            </div>
        </Layout>
    );
};

// ==================== Loan Car Status Badge ====================

/**
 * Dedicated status badge for the loan car tile.
 *
 * ALLOCATED → orange "Taken Out" badge with ArrowUpRight icon so staff
 * immediately see the car is with a customer and unavailable.
 * All other statuses use the standard colour scheme.
 */
const LoanCarStatusBadge = ({ status, activeIncident }) => {
    if (activeIncident) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                <ArrowUpRight size={12} />
                Taken Out
            </span>
        );
    }

    const styles = {
        AVAILABLE:   'bg-emerald-100 text-emerald-700',
        MAINTENANCE: 'bg-amber-100 text-amber-700',
        UNAVAILABLE: 'bg-gray-100 text-gray-700',
        RETIRED:     'bg-red-100 text-red-700',
    };
    const icons = {
        AVAILABLE:   <CheckCircle size={12} />,
        MAINTENANCE: <Wrench size={12} />,
    };

    return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
            {icons[status]}
            {status?.toLowerCase().replace('_', ' ')}
        </span>
    );
};

// ==================== Tab Components ====================

const LoanCarsTab = ({ loanCars, regoStatuses = {}, getStatusBadge, onEdit, onDelete, onShowHistory }) => {
    return (
        <>
            {loanCars.length === 0 ? (
                <EmptyState message="No loan cars added yet" />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {loanCars.map((car) => {
                        const isTakenOut = regoStatuses[car.rego] === true;
                        return (
                            <div
                                key={car.uuid}
                                className={`bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-shadow ${
                                    isTakenOut ? 'border-orange-200' : 'border-gray-200'
                                }`}
                            >
                                {/* Thin accent bar at top when car is out on loan */}
                                {isTakenOut && (
                                    <div className="h-1 bg-orange-400 w-full" />
                                )}
                                <div className="p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg font-bold text-gray-900">{car.rego}</span>
                                                {car.serviceOverdue && <AlertTriangle size={16} className="text-amber-500" />}
                                            </div>
                                            <p className="text-sm text-gray-500">{car.make} {car.model}</p>
                                        </div>
                                        <LoanCarStatusBadge status={car.status} activeIncident={regoStatuses[car.rego] === true} />
                                    </div>

                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center justify-between text-gray-500">
                                            <span className="flex items-center gap-1"><Fuel size={12} /> Fuel</span>
                                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${car.fuelLevel > 50 ? 'bg-emerald-500' : car.fuelLevel > 25 ? 'bg-amber-500' : 'bg-red-500'}`}
                                                    style={{ width: `${car.fuelLevel || 0}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between text-gray-500">
                                            <span className="flex items-center gap-1"><MapPin size={12} /> KM</span>
                                            <span className="font-medium text-gray-700">{car.currentMileage?.toLocaleString() || '—'}</span>
                                        </div>
                                        {car.rentalRate != null && (
                                            <div className="flex items-center justify-between text-gray-500">
                                                <span className="flex items-center gap-1"><Key size={12} /> Rate</span>
                                                <span className="font-medium text-gray-700">${Number(car.rentalRate).toFixed(2)}/day</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex gap-1 mt-4 pt-3 border-t border-gray-100">
                                        <button
                                            onClick={() => onShowHistory(car)}
                                            title="View loan history"
                                            className="flex-1 py-1.5 text-xs text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                                        >
                                            <History size={13} />
                                            <span className="hidden sm:inline">History</span>
                                        </button>
                                        <button
                                            onClick={() => onEdit(car)}
                                            className="flex-1 py-1.5 text-xs text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                                        >
                                            <Edit2 size={13} />
                                            <span className="hidden sm:inline">Edit</span>
                                        </button>
                                        <button
                                            onClick={() => onDelete(car)}
                                            className="flex-1 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                                        >
                                            <Trash2 size={13} />
                                            <span className="hidden sm:inline">Delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

// ==================== All Loan Cars Schedule Modal ====================

const AllLoanCarsScheduleModal = ({ loanCars, onClose }) => {
    const dispatch = useDispatch();
    const [windowStart, setWindowStart] = useState(() => startOfDay(new Date()));
    const windowEnd = addDays(windowStart, GANTT_WINDOW_DAYS);
    const [carAllocations, setCarAllocations] = useState({});
    const [loading, setLoading] = useState(true);
    const [tooltip, setTooltip] = useState(null);
    const days = Array.from({ length: GANTT_WINDOW_DAYS }, (_, i) => addDays(windowStart, i));
    const today = startOfDay(new Date());

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            const results = {};
            await Promise.all(
                loanCars.map(async (car) => {
                    try {
                        const action = await dispatch(fetchLoanCarAllocations(car.uuid));
                        results[car.uuid] = action.payload || [];
                    } catch {
                        results[car.uuid] = [];
                    }
                })
            );
            setCarAllocations(results);
            setLoading(false);
        };
        if (loanCars.length) fetchAll();
        else setLoading(false);
    }, [loanCars, dispatch]);

    const getAllocationBar = (allocation) => {
        const allocStart = new Date(allocation.allocationStart);
        const allocEnd   = new Date(allocation.allocationEnd);
        if (allocEnd <= windowStart || allocStart >= windowEnd) return null;
        const clampedStart = allocStart < windowStart ? windowStart : allocStart;
        const clampedEnd   = allocEnd   > windowEnd   ? windowEnd   : allocEnd;
        const windowMs = windowEnd - windowStart;
        return {
            left:  ((clampedStart - windowStart) / windowMs) * 100,
            width: ((clampedEnd - clampedStart)  / windowMs) * 100,
        };
    };

    const todayPosition = (() => {
        if (today < windowStart || today >= windowEnd) return null;
        return ((today - windowStart) / (windowEnd - windowStart)) * 100;
    })();

    const monthLabels = (() => {
        const labels = [];
        let current = null, startIdx = 0;
        days.forEach((day, idx) => {
            const label = day.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
            if (label !== current) {
                if (current !== null) labels.push({ label: current, startIdx, span: idx - startIdx });
                current = label; startIdx = idx;
            }
        });
        if (current) labels.push({ label: current, startIdx, span: GANTT_WINDOW_DAYS - startIdx });
        return labels;
    })();

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
            <div className="bg-white rounded-2xl w-full max-w-[96vw] xl:max-w-7xl max-h-[96vh] flex flex-col shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center">
                            <CalendarDays size={18} className="text-indigo-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">All Loan Car Schedules</h2>
                            <p className="text-sm text-gray-500">{loanCars.length} vehicle{loanCars.length !== 1 ? 's' : ''} · booking overview</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Date navigation */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-gray-50 shrink-0">
                    <button onClick={() => setWindowStart(d => addDays(d, -STEP_DAYS))}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-white border border-gray-200 rounded-lg transition-colors">
                        <ChevronLeft size={16} /> Prev
                    </button>
                    <div className="text-sm font-medium text-gray-700">
                        {formatDate(windowStart)} — {formatDate(addDays(windowEnd, -1))}
                    </div>
                    <button onClick={() => setWindowStart(d => addDays(d, STEP_DAYS))}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-white border border-gray-200 rounded-lg transition-colors">
                        Next <ChevronRight size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-auto px-6 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="animate-spin text-indigo-500" size={32} />
                            <span className="ml-3 text-gray-500">Loading schedules…</span>
                        </div>
                    ) : loanCars.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
                            <Info size={28} className="text-gray-300" />
                            <p className="text-sm">No loan cars found</p>
                        </div>
                    ) : (
                        <div className="relative">
                            {/* Month labels */}
                            <div className="flex mb-1 ml-28">
                                {monthLabels.map((m, i) => (
                                    <div key={i} className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate px-1"
                                         style={{ width: `${(m.span / GANTT_WINDOW_DAYS) * 100}%` }}>
                                        {m.label}
                                    </div>
                                ))}
                            </div>
                            {/* Day headers */}
                            <div className="flex border-b border-gray-200 pb-1 mb-2 ml-28">
                                {days.map((day, i) => {
                                    const isToday = day.toDateString() === today.toDateString();
                                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                                    return (
                                        <div key={i} className={`flex-1 text-center text-xs font-medium px-0.5 ${
                                            isToday ? 'text-indigo-600 font-bold' : isWeekend ? 'text-gray-400' : 'text-gray-500'
                                        }`}>
                                            <div>{day.getDate()}</div>
                                            <div className="text-gray-300 font-normal">{['S','M','T','W','T','F','S'][day.getDay()]}</div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Car rows */}
                            <div className="space-y-1">
                                {loanCars.map((car) => {
                                    const allocs = (carAllocations[car.uuid] || []).filter(a => {
                                        const s = new Date(a.allocationStart), e = new Date(a.allocationEnd);
                                        return e > windowStart && s < windowEnd;
                                    });
                                    return (
                                        <div key={car.uuid} className="flex items-center min-h-[44px]">
                                            {/* Car label */}
                                            <div className="w-28 shrink-0 pr-2">
                                                <div className="text-xs font-bold text-gray-800 truncate">{car.rego}</div>
                                                <div className="text-[10px] text-gray-400 truncate">{car.make} {car.model}</div>
                                            </div>
                                            {/* Gantt row */}
                                            <div className="flex-1 relative h-10">
                                                {/* Grid background */}
                                                <div className="absolute inset-0 flex pointer-events-none">
                                                    {days.map((day, i) => {
                                                        const isToday = day.toDateString() === today.toDateString();
                                                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                                                        return (
                                                            <div key={i} className={`flex-1 border-r ${
                                                                isToday ? 'bg-indigo-50/60 border-indigo-200'
                                                                    : isWeekend ? 'bg-gray-50 border-gray-100' : 'border-gray-100'
                                                            }`} />
                                                        );
                                                    })}
                                                </div>
                                                {/* Today line */}
                                                {todayPosition !== null && (
                                                    <div className="absolute top-0 bottom-0 w-0.5 bg-indigo-400 z-10 pointer-events-none"
                                                         style={{ left: `${todayPosition}%` }} />
                                                )}
                                                {/* Allocation bars */}
                                                {allocs.map((allocation) => {
                                                    const bar = getAllocationBar(allocation);
                                                    if (!bar) return null;
                                                    const color = BOOKING_STATUS_COLORS[allocation.bookingStatus] || BOOKING_STATUS_COLORS.DEFAULT;
                                                    const label = allocation.bookingCode || allocation.uuid?.slice(0, 8);
                                                    return (
                                                        <div
                                                            key={allocation.uuid}
                                                            className="absolute top-1 bottom-1 rounded-md cursor-pointer select-none shadow-sm hover:shadow-md transition-shadow overflow-hidden z-10"
                                                            style={{
                                                                left: `${bar.left}%`,
                                                                width: `max(${bar.width}%, 2%)`,
                                                                backgroundColor: color.bar,
                                                                border: `1px solid ${color.bar}`,
                                                            }}
                                                            onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, allocation })}
                                                            onMouseMove={(e) => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : t)}
                                                            onMouseLeave={() => setTooltip(null)}
                                                        >
                                                            <div className="flex items-center h-full px-1.5 gap-1 overflow-hidden" style={{ color: color.text }}>
                                                                <span className="text-xs font-bold whitespace-nowrap truncate">{label}</span>
                                                                {bar.width > 10 && (
                                                                    <span className="text-xs opacity-80 truncate">{allocation.customerName || ''}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {allocs.length === 0 && (
                                                    <div className="absolute inset-0 flex items-center">
                                                        <span className="text-[10px] text-gray-300 pl-1">No bookings</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {tooltip && <AllocationTooltip allocation={tooltip.allocation} x={tooltip.x} y={tooltip.y} />}
                        </div>
                    )}
                </div>

                {/* Legend */}
                <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl shrink-0">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs text-gray-500 font-medium mr-1">Status:</span>
                        {Object.entries(BOOKING_STATUS_COLORS).filter(([k]) => k !== 'DEFAULT').map(([status, color]) => (
                            <span key={status} className="flex items-center gap-1 text-xs">
                                <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color.bar }} />
                                <span className="text-gray-600 capitalize">{status.toLowerCase().replace('_', ' ')}</span>
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==================== Gantt / Schedule Modal ====================

const GANTT_WINDOW_DAYS = 28;
const STEP_DAYS = 14;

const BOOKING_STATUS_COLORS = {
    ENQUIRY:          { bar: '#a5b4fc', text: '#3730a3' },
    CONFIRMED:        { bar: '#6ee7b7', text: '#065f46' },
    VEHICLE_RECEIVED: { bar: '#67e8f9', text: '#164e63' },
    IN_PROGRESS:      { bar: '#fbbf24', text: '#78350f' },
    READY:            { bar: '#86efac', text: '#14532d' },
    COMPLETED:        { bar: '#94a3b8', text: '#1e293b' },
    CANCELLED:        { bar: '#fca5a5', text: '#7f1d1d' },
    DEFAULT:          { bar: '#c4b5fd', text: '#4c1d95' },
};

function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}
function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}
function formatDate(date) {
    return new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

const LoanCarScheduleModal = ({ car, allocations, loading, error, onClose }) => {
    const [windowStart, setWindowStart] = useState(() => startOfDay(new Date()));
    const windowEnd = addDays(windowStart, GANTT_WINDOW_DAYS);
    const [tooltip, setTooltip] = useState(null);
    const ganttRef = useRef(null);
    const days = Array.from({ length: GANTT_WINDOW_DAYS }, (_, i) => addDays(windowStart, i));
    const today = startOfDay(new Date());

    const getAllocationBar = (allocation) => {
        const allocStart = new Date(allocation.allocationStart);
        const allocEnd   = new Date(allocation.allocationEnd);
        if (allocEnd <= windowStart || allocStart >= windowEnd) return null;
        const clampedStart = allocStart < windowStart ? windowStart : allocStart;
        const clampedEnd   = allocEnd   > windowEnd   ? windowEnd   : allocEnd;
        const windowMs = windowEnd - windowStart;
        return {
            left:  ((clampedStart - windowStart) / windowMs) * 100,
            width: ((clampedEnd - clampedStart)  / windowMs) * 100,
        };
    };

    const getColor = (status) => BOOKING_STATUS_COLORS[status] || BOOKING_STATUS_COLORS.DEFAULT;

    const todayPosition = (() => {
        if (today < windowStart || today >= windowEnd) return null;
        return ((today - windowStart) / (windowEnd - windowStart)) * 100;
    })();

    const handleBarMouseEnter = (e, allocation) => setTooltip({ x: e.clientX, y: e.clientY, allocation });
    const handleBarMouseLeave = () => setTooltip(null);

    const monthLabels = (() => {
        const labels = [];
        let current = null, startIdx = 0;
        days.forEach((day, idx) => {
            const label = day.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
            if (label !== current) {
                if (current !== null) labels.push({ label: current, startIdx, span: idx - startIdx });
                current = label; startIdx = idx;
            }
        });
        if (current) labels.push({ label: current, startIdx, span: GANTT_WINDOW_DAYS - startIdx });
        return labels;
    })();

    const visibleAllocations = allocations.filter(a => {
        const s = new Date(a.allocationStart), e = new Date(a.allocationEnd);
        return e > windowStart && s < windowEnd;
    });

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
            <div className="bg-white rounded-2xl w-full max-w-[96vw] xl:max-w-7xl max-h-[96vh] flex flex-col shadow-2xl">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center">
                            <CalendarDays size={18} className="text-indigo-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Booking Schedule — {car?.rego}</h2>
                            <p className="text-sm text-gray-500">
                                {car?.make} {car?.model}
                                {car?.color ? ` · ${car.color}` : ''}
                                {car?.manufactureYear ? ` · ${car.manufactureYear}` : ''}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-gray-50 shrink-0">
                    <button onClick={() => setWindowStart(d => addDays(d, -STEP_DAYS))}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-white border border-gray-200 rounded-lg transition-colors">
                        <ChevronLeft size={16} /> Prev
                    </button>
                    <div className="text-sm font-medium text-gray-700">
                        {formatDate(windowStart)} — {formatDate(addDays(windowEnd, -1))}
                    </div>
                    <button onClick={() => setWindowStart(d => addDays(d, STEP_DAYS))}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-white border border-gray-200 rounded-lg transition-colors">
                        Next <ChevronRight size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-auto px-6 py-4">
                    {loading && (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="animate-spin text-indigo-500" size={32} />
                            <span className="ml-3 text-gray-500">Loading schedule…</span>
                        </div>
                    )}
                    {error && !loading && (
                        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                            <AlertCircle size={18} />{error}
                        </div>
                    )}
                    {!loading && !error && (
                        <div ref={ganttRef} className="relative">
                            <div className="flex mb-1">
                                {monthLabels.map((m, i) => (
                                    <div key={i} className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate px-1"
                                         style={{ width: `${(m.span / GANTT_WINDOW_DAYS) * 100}%` }}>
                                        {m.label}
                                    </div>
                                ))}
                            </div>
                            <div className="flex border-b border-gray-200 pb-1 mb-3">
                                {days.map((day, i) => {
                                    const isToday = day.toDateString() === today.toDateString();
                                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                                    return (
                                        <div key={i} className={`flex-1 text-center text-xs font-medium px-0.5 ${
                                            isToday ? 'text-indigo-600 font-bold' : isWeekend ? 'text-gray-400' : 'text-gray-500'
                                        }`}>
                                            <div>{day.getDate()}</div>
                                            <div className="text-gray-300 font-normal">{['S','M','T','W','T','F','S'][day.getDay()]}</div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="relative" style={{ minHeight: visibleAllocations.length === 0 ? 120 : undefined }}>
                                <div className="absolute inset-0 flex pointer-events-none">
                                    {days.map((day, i) => {
                                        const isToday = day.toDateString() === today.toDateString();
                                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                                        return (
                                            <div key={i} className={`flex-1 border-r ${
                                                isToday ? 'bg-indigo-50/60 border-indigo-200'
                                                    : isWeekend ? 'bg-gray-50 border-gray-100' : 'border-gray-100'
                                            }`} />
                                        );
                                    })}
                                </div>
                                {todayPosition !== null && (
                                    <div className="absolute top-0 bottom-0 w-0.5 bg-indigo-400 z-10 pointer-events-none"
                                         style={{ left: `${todayPosition}%` }}>
                                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-indigo-500 rounded-full" />
                                    </div>
                                )}
                                {visibleAllocations.length === 0 ? (
                                    <div className="relative z-10 flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
                                        <Info size={28} className="text-gray-300" />
                                        <p className="text-sm">No bookings in this period</p>
                                    </div>
                                ) : (
                                    <div className="relative z-10 space-y-2 py-1">
                                        {visibleAllocations.map((allocation) => {
                                            const bar = getAllocationBar(allocation);
                                            if (!bar) return null;
                                            const color = getColor(allocation.bookingStatus);
                                            const label = allocation.bookingCode || allocation.uuid?.slice(0, 8);
                                            return (
                                                <div key={allocation.uuid} className="relative h-10">
                                                    <div className="absolute inset-y-0 left-0 right-0 rounded-lg bg-transparent" />
                                                    <div
                                                        className="absolute top-1 bottom-1 rounded-md cursor-pointer select-none shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                                                        style={{
                                                            left: `${bar.left}%`,
                                                            width: `max(${bar.width}%, 2%)`,
                                                            backgroundColor: color.bar,
                                                            border: `1px solid ${color.bar}`,
                                                        }}
                                                        onMouseEnter={(e) => handleBarMouseEnter(e, allocation)}
                                                        onMouseMove={(e) => setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : t)}
                                                        onMouseLeave={handleBarMouseLeave}
                                                    >
                                                        <div className="flex items-center h-full px-2 gap-1.5 overflow-hidden" style={{ color: color.text }}>
                                                            <span className="text-xs font-bold whitespace-nowrap truncate">{label}</span>
                                                            {bar.width > 8 && (
                                                                <span className="text-xs opacity-80 truncate">{allocation.customerName || 'Customer'}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            {tooltip && <AllocationTooltip allocation={tooltip.allocation} x={tooltip.x} y={tooltip.y} />}
                        </div>
                    )}
                </div>

                {!loading && !error && (
                    <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl shrink-0">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-xs text-gray-500 font-medium mr-1">Status:</span>
                            {Object.entries(BOOKING_STATUS_COLORS).filter(([k]) => k !== 'DEFAULT').map(([status, color]) => (
                                <span key={status} className="flex items-center gap-1 text-xs">
                                    <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color.bar }} />
                                    <span className="text-gray-600 capitalize">{status.toLowerCase().replace('_', ' ')}</span>
                                </span>
                            ))}
                            <span className="ml-auto text-xs text-gray-500">
                                {allocations.length} allocation{allocations.length !== 1 ? 's' : ''} total
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const AllocationTooltip = ({ allocation, x, y }) => {
    const status = allocation.bookingStatus;
    const color  = BOOKING_STATUS_COLORS[status] || BOOKING_STATUS_COLORS.DEFAULT;
    const TOOLTIP_W = 272, TOOLTIP_H = 280;
    const flipLeft = x + TOOLTIP_W + 12 > window.innerWidth;
    const flipUp   = y + TOOLTIP_H + 12 > window.innerHeight;
    const startDt = new Date(allocation.allocationStart);
    const endDt   = new Date(allocation.allocationEnd);
    const fmtDate = (d) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    const fmtTime = (d) => d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });

    return (
        <div className="fixed z-[9999] bg-gray-900 text-white rounded-xl shadow-2xl p-3 text-xs pointer-events-none"
             style={{ left: flipLeft ? x - TOOLTIP_W - 8 : x + 12, top: flipUp ? y - TOOLTIP_H - 8 : y + 12, width: TOOLTIP_W }}>
            <div className="flex items-center gap-2 mb-2.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: color.bar }} />
                <span className="font-bold text-sm truncate">{allocation.bookingCode || 'Booking'}</span>
                <span className="ml-auto text-xs px-1.5 py-0.5 rounded-full font-medium capitalize shrink-0"
                      style={{ backgroundColor: color.bar + '33', color: color.bar }}>
                    {status?.toLowerCase().replace(/_/g, ' ') || '—'}
                </span>
            </div>
            <div className="space-y-1.5 text-gray-300">
                {allocation.bookingCode && (
                    <div className="flex justify-between gap-2">
                        <span className="text-gray-400 shrink-0">Booking ID</span>
                        <span className="text-indigo-300 font-mono text-xs truncate">{allocation.bookingCode}</span>
                    </div>
                )}
                {allocation.customerName && (
                    <div className="flex justify-between gap-2">
                        <span className="text-gray-400 shrink-0">Customer</span>
                        <span className="text-white font-medium truncate">{allocation.customerName}</span>
                    </div>
                )}
                {allocation.vehicleRegistration && (
                    <div className="flex justify-between gap-2">
                        <span className="text-gray-400 shrink-0">Vehicle</span>
                        <span className="text-white font-medium">{allocation.vehicleRegistration}</span>
                    </div>
                )}
                <div className="border-t border-gray-700 pt-1.5 mt-0.5 space-y-1">
                    <div className="text-gray-500 uppercase tracking-wider text-[10px] font-semibold mb-0.5">Pick-up</div>
                    <div className="flex justify-between gap-2"><span className="text-gray-400 shrink-0">Date</span><span className="text-white">{fmtDate(startDt)}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-gray-400 shrink-0">Time</span><span className="text-emerald-300 font-semibold">{fmtTime(startDt)}</span></div>
                </div>
                <div className="border-t border-gray-700 pt-1.5 space-y-1">
                    <div className="text-gray-500 uppercase tracking-wider text-[10px] font-semibold mb-0.5">Return</div>
                    <div className="flex justify-between gap-2"><span className="text-gray-400 shrink-0">Date</span><span className="text-white">{fmtDate(endDt)}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-gray-400 shrink-0">Time</span><span className="text-amber-300 font-semibold">{fmtTime(endDt)}</span></div>
                </div>
                {allocation.notes && (
                    <div className="border-t border-gray-700 pt-1.5 text-gray-400 italic leading-relaxed">{allocation.notes}</div>
                )}
            </div>
        </div>
    );
};

// ==================== Job Allocations Panel ====================

const JobAllocationsPanel = ({ resourceUuid, isAllocated, resourceType }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [confirmJobUuid, setConfirmJobUuid] = useState(null);
    const [unassigning, setUnassigning] = useState(null);
    const [error, setError] = useState(null);

    const load = async () => {
        if (!open) return;
        setLoading(true); setError(null);
        try { setAllocations(await getResourceJobAllocationsApi(resourceUuid)); }
        catch { setError('Failed to load job allocations'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [open]);

    const handleUnassign = async (jobUuid) => {
        setUnassigning(jobUuid); setConfirmJobUuid(null);
        try {
            await unassignResourceFromJobApi(resourceUuid, jobUuid);
            setAllocations(prev => prev.filter(a => a.jobUuid !== jobUuid));
            const refreshMap = { equipment: fetchEquipment, tools: fetchTools, spaces: fetchSpaces, consultants: fetchConsultants };
            if (refreshMap[resourceType]) dispatch(refreshMap[resourceType]());
        } catch { setError('Failed to unassign resource'); }
        finally { setUnassigning(null); }
    };

    const statusColors = {
        QUEUED: 'bg-gray-100 text-gray-600', IN_PROGRESS: 'bg-blue-100 text-blue-700',
        PAUSED: 'bg-amber-100 text-amber-700', COMPLETED: 'bg-emerald-100 text-emerald-700',
        CANCELLED: 'bg-red-100 text-red-700',
    };

    if (!isAllocated && !open) return null;

    return (
        <div className="mt-2">
            <button onClick={() => setOpen(o => !o)}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium">
                <Briefcase size={12} />
                {isAllocated ? 'View job assignments' : 'Job assignments'}
                <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 p-3">
                    {loading && <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 size={12} className="animate-spin" /> Loading...</div>}
                    {error && <p className="text-xs text-red-600">{error}</p>}
                    {!loading && allocations.length === 0 && <p className="text-xs text-gray-500">No active job assignments</p>}
                    {!loading && allocations.map(a => (
                        <div key={a.allocationUuid} className="border-b border-blue-100 last:border-0 py-1.5">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-xs font-mono font-semibold text-gray-800 shrink-0">{a.jobNumber}</span>
                                    {a.jobTitle && <span className="text-xs text-gray-600 truncate">{a.jobTitle}</span>}
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium shrink-0 ${statusColors[a.jobStatus] || 'bg-gray-100 text-gray-600'}`}>
                                        {a.jobStatus?.replace('_', ' ')}
                                    </span>
                                </div>
                                <button onClick={() => setConfirmJobUuid(a.jobUuid)} disabled={!!unassigning} title="Remove from job"
                                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded shrink-0 disabled:opacity-40">
                                    {unassigning === a.jobUuid ? <Loader2 size={12} className="animate-spin" /> : <Unlink size={12} />}
                                </button>
                            </div>
                            {confirmJobUuid === a.jobUuid && (
                                <div className="mt-1.5 flex items-center gap-2 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                                    <span className="text-xs text-red-700 flex-1">Remove from {a.jobNumber}?</span>
                                    <button onClick={() => handleUnassign(a.jobUuid)} className="px-2 py-0.5 bg-red-600 text-white text-xs rounded hover:bg-red-700">Remove</button>
                                    <button onClick={() => setConfirmJobUuid(null)} className="px-2 py-0.5 bg-white border border-gray-300 text-xs rounded hover:bg-gray-50">Cancel</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ==================== Other Tab Components ====================

const EquipmentTab = ({ equipment, getStatusBadge, onEdit, onDelete }) => (
    equipment.length === 0 ? <EmptyState message="No equipment added yet" /> : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <table className="w-full">
                <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Rental Rate</th>
                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
                </thead>
                <tbody>
                {equipment.map((item) => (
                    <tr key={item.uuid} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="p-4">
                            <div className="font-medium text-gray-900">{item.name}</div>
                            <JobAllocationsPanel resourceUuid={item.uuid} isAllocated={item.status === 'ALLOCATED'} resourceType="equipment" />
                        </td>
                        <td className="p-4 text-sm text-gray-600">{item.resourceIdentifier}</td>
                        <td className="p-4 text-sm text-gray-600">{item.category || '—'}</td>
                        <td className="p-4 text-center">{getStatusBadge(item.status)}</td>
                        <td className="p-4 text-right font-medium text-gray-900">{item.rentalRate ? `$${item.rentalRate}/day` : '—'}</td>
                        <td className="p-4 text-sm text-gray-500">{item.location || '—'}</td>
                        <td className="p-4 text-right">
                            <button onClick={() => onEdit(item)} className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded-lg mr-1"><Edit2 size={16} /></button>
                            <button onClick={() => onDelete(item)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    )
);

const ToolsTab = ({ tools, getConditionBadge, onEdit, onDelete }) => {
    const stats = {
        totalCost: tools.reduce((sum, t) => sum + (t.purchaseCost || 0), 0),
        currentValue: tools.reduce((sum, t) => sum + (t.currentValue || 0), 0),
    };
    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <StatCard label="Total Asset Value" value={`$${stats.totalCost.toLocaleString()}`} />
                <StatCard label="Current Value" value={`$${stats.currentValue.toLocaleString()}`} color="emerald" />
                <StatCard label="Total Depreciation" value={`-$${(stats.totalCost - stats.currentValue).toLocaleString()}`} color="red" />
            </div>
            {tools.length === 0 ? <EmptyState message="No tools added yet" /> : (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <table className="w-full">
                        <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Tool</th>
                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Purchase Date</th>
                            <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Original Cost</th>
                            <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Current Value</th>
                            <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Depreciation</th>
                            <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Condition</th>
                            <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {tools.map((tool) => (
                            <tr key={tool.uuid} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="p-4">
                                    <div className="font-medium text-gray-900">{tool.name}</div>
                                    <JobAllocationsPanel resourceUuid={tool.uuid} isAllocated={tool.status === 'ALLOCATED'} resourceType="tools" />
                                </td>
                                <td className="p-4 text-sm text-gray-600">{tool.resourceIdentifier}</td>
                                <td className="p-4 text-sm text-gray-600">{tool.purchaseDate || '—'}</td>
                                <td className="p-4 text-right font-medium text-gray-900">{tool.purchaseCost ? `$${tool.purchaseCost.toLocaleString()}` : '—'}</td>
                                <td className="p-4 text-right font-medium text-emerald-600">{tool.currentValue ? `$${tool.currentValue.toLocaleString()}` : '—'}</td>
                                <td className="p-4 text-center">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${tool.depreciationPercentage > 30 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                                        -{tool.depreciationPercentage?.toFixed(0) || 0}%
                                    </span>
                                </td>
                                <td className="p-4 text-center">{getConditionBadge(tool.condition)}</td>
                                <td className="p-4 text-right">
                                    <button onClick={() => onEdit(tool)} className="p-1.5 text-cyan-600 hover:bg-cyan-50 rounded-lg mr-1"><Edit2 size={16} /></button>
                                    <button onClick={() => onDelete(tool)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

const SpacesTab = ({ spaces, getStatusBadge, onEdit, onDelete }) => {
    const stats = {
        total: spaces.length,
        available: spaces.filter(s => s.status === 'AVAILABLE').length,
        allocated: spaces.filter(s => s.status === 'ALLOCATED').length,
    };
    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <StatCard label="Total Spaces" value={stats.total} />
                <StatCard label="Available" value={stats.available} color="emerald" />
                <StatCard label="In Use" value={stats.allocated} color="blue" />
            </div>
            {spaces.length === 0 ? <EmptyState message="No spaces added yet" /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {spaces.map((space) => (
                        <div key={space.uuid} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="font-bold text-gray-900">{space.name}</h3>
                                    <p className="text-sm text-gray-500">{space.resourceIdentifier}</p>
                                </div>
                                {getStatusBadge(space.status)}
                            </div>
                            <div className="space-y-2 text-sm mb-4">
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Building2 size={14} />
                                    <span>{space.spaceType?.replace('_', ' ') || 'Space'}</span>
                                    {space.zone && <span className="text-gray-400">• {space.zone}</span>}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {space.hasLift && <FeatureBadge icon={<Zap size={12} />} label="Lift" />}
                                    {space.hasCompressedAir && <FeatureBadge icon={<Wind size={12} />} label="Air" />}
                                    {space.hasPower && <FeatureBadge icon={<Zap size={12} />} label="Power" />}
                                    {space.hasDrainage && <FeatureBadge icon={<Droplets size={12} />} label="Drainage" />}
                                </div>
                                {space.maxVehicleSize && <div className="text-gray-500">Max: {space.maxVehicleSize?.toLowerCase()} vehicles</div>}
                            </div>
                            <JobAllocationsPanel resourceUuid={space.uuid} isAllocated={space.status === 'ALLOCATED'} resourceType="spaces" />
                            <div className="flex gap-2 pt-3 border-t border-gray-100 mt-2">
                                <button onClick={() => onEdit(space)} className="flex-1 py-1.5 text-sm text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors">
                                    <Edit2 size={14} className="inline mr-1" /> Edit
                                </button>
                                <button onClick={() => onDelete(space)} className="flex-1 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                    <Trash2 size={14} className="inline mr-1" /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

const ConsultantsTab = ({ consultants, getStatusBadge, onEdit, onDelete }) => (
    consultants.length === 0 ? <EmptyState message="No consultants added yet" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {consultants.map((consultant) => (
                <div key={consultant.uuid} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                        <div>
                            <h3 className="font-bold text-gray-900">{consultant.contactName}</h3>
                            {consultant.businessName && <p className="text-sm text-gray-500">{consultant.businessName}</p>}
                        </div>
                        {getStatusBadge(consultant.status)}
                    </div>
                    <div className="space-y-2 text-sm mb-4">
                        {consultant.speciality && (
                            <div className="inline-block px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">{consultant.speciality}</div>
                        )}
                        <div className="flex items-center gap-2 text-gray-600"><Phone size={14} /><span>{consultant.phone || consultant.primaryPhone}</span></div>
                        {consultant.email && (
                            <div className="flex items-center gap-2 text-gray-600"><Mail size={14} /><span className="truncate">{consultant.email}</span></div>
                        )}
                        {consultant.hourlyRate && (
                            <div className="text-gray-600 font-medium">
                                ${consultant.hourlyRate}/hr{consultant.calloutFee && ` + $${consultant.calloutFee} callout`}
                            </div>
                        )}
                        {consultant.rating && (
                            <div className="flex items-center gap-1 text-amber-500"><Star size={14} fill="currentColor" /><span>{consultant.rating.toFixed(1)}</span></div>
                        )}
                    </div>
                    <JobAllocationsPanel resourceUuid={consultant.uuid} isAllocated={consultant.status === 'ALLOCATED'} resourceType="consultants" />
                    <div className="flex gap-2 pt-3 border-t border-gray-100 mt-2">
                        <button onClick={() => onEdit(consultant)} className="flex-1 py-1.5 text-sm text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors">
                            <Edit2 size={14} className="inline mr-1" /> Edit
                        </button>
                        <button onClick={() => onDelete(consultant)} className="flex-1 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 size={14} className="inline mr-1" /> Delete
                        </button>
                    </div>
                </div>
            ))}
        </div>
    )
);

// ==================== Helper Components ====================

const StatCard = ({ label, value, color }) => {
    const colorClasses = { emerald: 'text-emerald-600', blue: 'text-blue-600', amber: 'text-amber-600', red: 'text-red-600' };
    return (
        <div className="bg-white p-4 rounded-xl border border-gray-200">
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className={`text-2xl font-bold ${color ? colorClasses[color] : 'text-gray-900'}`}>{value}</p>
        </div>
    );
};

const EmptyState = ({ message }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={24} className="text-gray-400" />
        </div>
        <p className="text-gray-500">{message}</p>
        <p className="text-sm text-gray-400 mt-1">Click "Add" to create your first resource</p>
    </div>
);

const FeatureBadge = ({ icon, label }) => (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{icon} {label}</span>
);

// ==================== Modal Components ====================

const ResourceModal = ({ type, item, onClose, onSubmit, loading, error }) => {
    const [formData, setFormData] = useState(item || {});

    const handleChange = (e) => {
        const { name, value, type: inputType, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: inputType === 'checkbox' ? checked : inputType === 'number' ? (value === '' ? null : parseFloat(value)) : value
        }));
    };

    const handleSubmit = (e) => { e.preventDefault(); onSubmit(formData); };

    const renderFields = () => {
        switch (type) {
            case 'loan-cars': return (
                <>
                    <FormField label="Registration (Rego)" name="rego" value={formData.rego} onChange={handleChange} required />
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Make" name="make" value={formData.make} onChange={handleChange} />
                        <FormField label="Model" name="model" value={formData.model} onChange={handleChange} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Year" name="manufactureYear" type="number" value={formData.manufactureYear} onChange={handleChange} />
                        <FormField label="Color" name="color" value={formData.color} onChange={handleChange} />
                    </div>
                    <FormField label="Rental Rate ($/day)" name="rentalRate" type="number" value={formData.rentalRate} onChange={handleChange} placeholder="0.00" />
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Current KM" name="currentMileage" type="number" value={formData.currentMileage} onChange={handleChange} />
                        <FormField label="Fuel Level (%)" name="fuelLevel" type="number" value={formData.fuelLevel} onChange={handleChange} />
                    </div>
                    <FormField label="Next Service Due" name="nextServiceDue" type="date" value={formData.nextServiceDue} onChange={handleChange} />
                    <FormField label="Rego Expiry" name="regoExpiry" type="date" value={formData.regoExpiry} onChange={handleChange} />
                    <FormField label="Known Issues" name="knownIssues" value={formData.knownIssues} onChange={handleChange} multiline />
                </>
            );
            case 'equipment': return (
                <>
                    <FormField label="Equipment Name" name="name" value={formData.name} onChange={handleChange} required />
                    <FormField label="Equipment ID" name="resourceIdentifier" value={formData.resourceIdentifier} onChange={handleChange} required />
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Category" name="category" value={formData.category} onChange={handleChange} />
                        <FormField label="Brand" name="brand" value={formData.brand} onChange={handleChange} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Purchase Date" name="purchaseDate" type="date" value={formData.purchaseDate} onChange={handleChange} />
                        <FormField label="Purchase Cost" name="purchaseCost" type="number" value={formData.purchaseCost} onChange={handleChange} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Rental Rate ($/day)" name="rentalRate" type="number" value={formData.rentalRate} onChange={handleChange} />
                        <FormField label="Location" name="location" value={formData.location} onChange={handleChange} />
                    </div>
                    <FormField label="Operating Notes" name="operatingNotes" value={formData.operatingNotes} onChange={handleChange} multiline />
                </>
            );
            case 'tools': return (
                <>
                    <FormField label="Tool Name" name="name" value={formData.name} onChange={handleChange} required />
                    <FormField label="Tool ID" name="resourceIdentifier" value={formData.resourceIdentifier} onChange={handleChange} required />
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Category" name="category" value={formData.category} onChange={handleChange} />
                        <FormField label="Brand" name="brand" value={formData.brand} onChange={handleChange} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Purchase Date" name="purchaseDate" type="date" value={formData.purchaseDate} onChange={handleChange} />
                        <FormField label="Purchase Cost" name="purchaseCost" type="number" value={formData.purchaseCost} onChange={handleChange} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Current Value" name="currentValue" type="number" value={formData.currentValue} onChange={handleChange} />
                        <FormField label="Depreciation Rate (%)" name="depreciationRate" type="number" value={formData.depreciationRate} onChange={handleChange} />
                    </div>
                    <FormSelect label="Condition" name="condition" value={formData.condition} onChange={handleChange}
                                options={['EXCELLENT', 'GOOD', 'FAIR', 'POOR', 'NEEDS_REPLACEMENT']} />
                    <FormField label="Assigned To Staff" name="assignedToStaff" value={formData.assignedToStaff} onChange={handleChange} />
                </>
            );
            case 'spaces': return (
                <>
                    <FormField label="Space Name" name="name" value={formData.name} onChange={handleChange} required />
                    <FormField label="Space ID" name="resourceIdentifier" value={formData.resourceIdentifier} onChange={handleChange} required />
                    <div className="grid grid-cols-2 gap-4">
                        <FormSelect label="Space Type" name="spaceType" value={formData.spaceType} onChange={handleChange}
                                    options={['BAY', 'HOIST', 'PIT', 'WASH_BAY', 'PAINT_BOOTH', 'ALIGNMENT_BAY', 'INSPECTION_BAY', 'STORAGE', 'OTHER']} />
                        <FormField label="Zone" name="zone" value={formData.zone} onChange={handleChange} />
                    </div>
                    <FormSelect label="Max Vehicle Size" name="maxVehicleSize" value={formData.maxVehicleSize} onChange={handleChange}
                                options={['SMALL', 'MEDIUM', 'LARGE', 'COMMERCIAL', 'HEAVY']} />
                    <div className="grid grid-cols-2 gap-4">
                        <FormCheckbox label="Has Lift" name="hasLift" checked={formData.hasLift} onChange={handleChange} />
                        <FormCheckbox label="Has Compressed Air" name="hasCompressedAir" checked={formData.hasCompressedAir} onChange={handleChange} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <FormCheckbox label="Has Power" name="hasPower" checked={formData.hasPower} onChange={handleChange} />
                        <FormCheckbox label="Has Drainage" name="hasDrainage" checked={formData.hasDrainage} onChange={handleChange} />
                    </div>
                    <FormField label="Special Features" name="specialFeatures" value={formData.specialFeatures} onChange={handleChange} multiline />
                </>
            );
            case 'consultants': return (
                <>
                    <FormField label="Contact Name" name="contactName" value={formData.contactName} onChange={handleChange} required />
                    <FormField label="Business Name" name="businessName" value={formData.businessName} onChange={handleChange} />
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Phone" name="phone" value={formData.phone} onChange={handleChange} required />
                        <FormField label="Mobile" name="mobilePhone" value={formData.mobilePhone} onChange={handleChange} />
                    </div>
                    <FormField label="Email" name="email" type="email" value={formData.email} onChange={handleChange} />
                    <FormField label="Speciality" name="speciality" value={formData.speciality} onChange={handleChange} />
                    <FormField label="Services" name="services" value={formData.services} onChange={handleChange} multiline />
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Hourly Rate ($)" name="hourlyRate" type="number" value={formData.hourlyRate} onChange={handleChange} />
                        <FormField label="Callout Fee ($)" name="calloutFee" type="number" value={formData.calloutFee} onChange={handleChange} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Availability Days" name="availabilityDays" value={formData.availabilityDays} onChange={handleChange} placeholder="e.g., Mon-Fri" />
                        <FormField label="Working Hours" name="workingHours" value={formData.workingHours} onChange={handleChange} placeholder="e.g., 8am-5pm" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <FormCheckbox label="Has Insurance" name="hasInsurance" checked={formData.hasInsurance} onChange={handleChange} />
                        <FormCheckbox label="Has License" name="hasLicense" checked={formData.hasLicense} onChange={handleChange} />
                    </div>
                    <FormField label="Notes" name="notes" value={formData.notes} onChange={handleChange} multiline />
                </>
            );
            default: return null;
        }
    };

    const titles = {
        'loan-cars': item ? 'Edit Loan Car' : 'Add Loan Car',
        'equipment': item ? 'Edit Equipment' : 'Add Equipment',
        'tools': item ? 'Edit Tool' : 'Add Tool',
        'spaces': item ? 'Edit Space' : 'Add Space',
        'consultants': item ? 'Edit Consultant' : 'Add Consultant',
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-900">{titles[type]}</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
                    {renderFields()}
                    <div className="flex gap-3 pt-4">
                        <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading} className="flex-1 py-2.5 bg-cyan-600 text-white rounded-xl font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                            {loading && <Loader2 size={18} className="animate-spin" />}
                            {item ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const DeleteConfirmModal = ({ item, onClose, onConfirm, loading }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={24} className="text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 text-center mb-2">Delete Resource?</h2>
            <p className="text-gray-500 text-center mb-6">
                Are you sure you want to delete <span className="font-medium text-gray-900">{item?.name || item?.rego || item?.contactName}</span>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors">Cancel</button>
                <button onClick={onConfirm} disabled={loading} className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading && <Loader2 size={18} className="animate-spin" />}
                    Delete
                </button>
            </div>
        </div>
    </div>
);

// ==================== Form Components ====================

const FormField = ({ label, name, type = 'text', value, onChange, required, multiline, placeholder }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        {multiline ? (
            <textarea name={name} value={value || ''} onChange={onChange} required={required} placeholder={placeholder} rows={3}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none" />
        ) : (
            <input type={type} name={name} value={value || ''} onChange={onChange} required={required} placeholder={placeholder}
                   className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500" />
        )}
    </div>
);

const FormSelect = ({ label, name, value, onChange, options }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <select name={name} value={value || ''} onChange={onChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500">
            <option value="">Select...</option>
            {options.map(opt => <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>)}
        </select>
    </div>
);

const FormCheckbox = ({ label, name, checked, onChange }) => (
    <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" name={name} checked={checked || false} onChange={onChange}
               className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500" />
        <span className="text-sm text-gray-700">{label}</span>
    </label>
);

export default ResourcesPage;